import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useIspaiNetworkStats = () => {
  return useQuery({
    queryKey: ['ispai-network-stats'],
    queryFn: async () => {
      // Get total participants (users with profiles)
      const { count: totalParticipants } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get active contributors (users who created KB, Solution, Call, Sprint, or sent invitations)
      const { data: kbCreators } = await supabase
        .from('knowledge_bases')
        .select('creator_id')
        .eq('status', 'published');

      const { data: solutionCreators } = await supabase
        .from('solutions')
        .select('creator_id')
        .eq('status', 'published');

      const { data: callCreators } = await supabase
        .from('calls')
        .select('creator_id');

      const { data: sprintCreators } = await supabase
        .from('prompt_architecture_sprints')
        .select('creator_id');

      const { data: inviterIds } = await supabase
        .from('invitations')
        .select('inviter_id');

      // Combine and deduplicate creator IDs
      const allCreatorIds = new Set([
        ...(kbCreators?.map(k => k.creator_id) || []),
        ...(solutionCreators?.map(s => s.creator_id) || []),
        ...(callCreators?.map(c => c.creator_id) || []),
        ...(sprintCreators?.map(s => s.creator_id) || []),
        ...(inviterIds?.map(i => i.inviter_id) || [])
      ]);

      const activeContributors = allCreatorIds.size;

      // Get published/minted knowledge bases count
      const { count: knowledgeBases } = await supabase
        .from('knowledge_bases')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      // Get published/minted solutions count
      const { count: totalSolutions } = await supabase
        .from('solutions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      // Get department distribution based on user expertise
      const { data: expertiseData } = await supabase
        .from('user_expertise')
        .select('domain_name, user_id');

      // Count unique users per domain
      const departmentMap: Record<string, Set<string>> = {};
      expertiseData?.forEach(exp => {
        if (!departmentMap[exp.domain_name]) {
          departmentMap[exp.domain_name] = new Set();
        }
        departmentMap[exp.domain_name].add(exp.user_id);
      });

      // Convert to array and take top 5
      const departments = Object.entries(departmentMap)
        .map(([name, userIds]) => ({
          name,
          participants: userIds.size
        }))
        .sort((a, b) => b.participants - a.participants)
        .slice(0, 5);

      // If no expertise data, return empty departments
      const finalDepartments = departments.length > 0 ? departments : [
        { name: "No data yet", participants: 0 }
      ];

      return {
        totalParticipants: totalParticipants || 0,
        activeContributors,
        knowledgeBases: knowledgeBases || 0,
        totalSolutions: totalSolutions || 0,
        departments: finalDepartments
      };
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });
};
